import type {
    JobStats,
    SessionMinMax,
} from "@mrflark/core/hooks";
import type {
    ComponentChildren,
} from "preact";
import {
    Card,
    CardContent,
} from "../mod.tsx";

type JobSummaryCardsProps = {
    stats: JobStats | null;
    sessionRanges: Record<string, SessionMinMax>;
    extraCards?: ComponentChildren;
};

const RangeAnnotation = ({
    range,
}: {
    range: SessionMinMax | undefined;
}): ComponentChildren => {
    if (!range || range.min === range.max) return null;
    return (
        <span
            className="text-text-muted text-xs ml-1"
            title={`Session range: ${range.min}\u2013${range.max}`}
        >
            ({range.min}&ndash;{range.max})
        </span>
    );
};

const StatRow = ({
    label,
    value,
    range,
    color,
}: {
    label: string;
    value: number;
    range?: SessionMinMax;
    color?: string;
}): ComponentChildren => (
    <div className="flex items-center justify-between text-sm">
        <span className="text-text-secondary">{label}</span>
        <span className={color ?? "text-text-primary"}>
            {value}
            <RangeAnnotation range={range} />
        </span>
    </div>
);

export const JobSummaryCards = ({
    stats,
    sessionRanges,
    extraCards,
}: JobSummaryCardsProps): ComponentChildren => {
    return (
        <div className="space-y-3">
            <Card>
                <CardContent className="space-y-1.5">
                    <h3 className="text-sm font-medium text-text-primary mb-2">
                        Jobs
                    </h3>
                    {stats
                        ? (
                            <>
                                <StatRow
                                    label="Pending"
                                    value={stats.pending}
                                    range={sessionRanges.pending}
                                />
                                <StatRow
                                    label="Processing"
                                    value={stats.processing}
                                    range={sessionRanges.processing}
                                    color="text-yellow-400"
                                />
                                <StatRow
                                    label="Completed"
                                    value={stats.completed}
                                />
                                <StatRow
                                    label="Failed"
                                    value={stats.failed}
                                    range={sessionRanges.failed}
                                    color={stats.failed > 0
                                        ? "text-red-400"
                                        : undefined}
                                />
                                <div className="border-t border-border pt-1.5 mt-1.5">
                                    <StatRow
                                        label="Total"
                                        value={stats.total}
                                    />
                                </div>
                            </>
                        )
                        : (
                            <span className="text-text-secondary text-sm">
                                Loading...
                            </span>
                        )}
                </CardContent>
            </Card>

            {extraCards}
        </div>
    );
};
